import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useCustomSnackbar } from '../hooks/useCustomSneakbar';
import Swal from 'sweetalert2';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { generateAxiosInstanceInstitute } from '../hooks/useAuthorizedRequestForInstitute';
import { generateAxiosInstanceSection } from '../hooks/useAuthorizedRequestForSection';
import { generateAxiosInstanceCommon } from '../hooks/useAuthorizedReuestCommon';
import { useNavigate } from 'react-router-dom';
import { GeneratePdf } from '../components/GeneratePdf';
import { Loader } from '../components/ModalsCommonFunctions';
import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import Pagination from '../components/Pagination';

const TeacherCredentials = () => {
    const authorizedAxios = generateAxiosInstance();
    const authorizedAxiosInstitute = generateAxiosInstanceInstitute();
    const authorizedAxiosSection = generateAxiosInstanceSection();
    const authorizedAxiosCommon = generateAxiosInstanceCommon();
    const initialForm = {
        instituteMasterId: '',
        sectionMasterId: ''
    }
    const [teacherData, setTeacherData] = useState(initialForm);
    const [instituteList, setInstituteList] = useState([]);
    const [sectionList, setSectionList] = useState([]);
    const [credentials, setCredentials] = useState([]);
    const [showPassword, setShowPassword] = useState([]);

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);
    const [refresh, setRefresh] = useState(true);
    const [addNew, setAddNew] = useState(false);

    const [teacherNameSort, setTeacherNameSort] = useState(null);
    const [userIdSort, setUserIdSort] = useState(null);
    const [emailSort, setEmailSort] = useState(null);
    const [genderSort, setGenderSort] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["teacher.teacherName", "teacher.email", "teacher.contactNumber", "teacher.gender", "userId"];

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        getInstituteList();
    }, []);

    useEffect(() => {
        getCredentials();
    }, [teacherData]);

    const getInstituteList = () => {
        const apiUrlForInstituteList = `/get-institute-master`;
        authorizedAxios && authorizedAxios.get(apiUrlForInstituteList)
            .then(response => {
                setInstituteList(response.data.result);
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            })
    }

    const handleInsituteChange = (e) => {
        const { name, value } = e.target;
        setTeacherData(prevData => {
            return {
                ...prevData,
                [name]: value,
                sectionMasterId: ''
            }
        })
        setSectionList([]);
        setCredentials([]);

        const apiUrl = `/get-section?instituteMasterId=${value}`;
        authorizedAxiosInstitute && authorizedAxiosInstitute.get(apiUrl)
            .then(response => {
                setSectionList(response.data.result)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTeacherData((prevData) => {
            return {
                ...prevData,
                [name]: value
            }
        })
    }

    const getApiUrl = () => {
        return `/get-teachers-details?instituteMasterId=${teacherData.instituteMasterId}&sectionMasterId=${teacherData.sectionMasterId}`;
    }

    const getCredentials = async () => {
        if (teacherData.instituteMasterId.length !== 0 && teacherData.sectionMasterId.length !== 0) {
            try {
                setLoading(true);
                const response = await authorizedAxiosSection.get(getApiUrl());
                setCredentials(response.data.data);
                // setShowPassword(Array(response.data.data.length).fill(false));
            } catch (error) {
                const errorMsg = error?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            } finally {
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            setRefresh(!refresh);
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            if (teacherData.instituteMasterId.length !== 0 && teacherData.sectionMasterId.length !== 0) {
                setCurrPage(1);
                setCredentials(await searchArray("B", getApiUrl(), searchKeys, searchValue.trim(), "data"));
            }
        }
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(credentials, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId, addNew])

    useEffect(() => {
        setTotalPages(Math.ceil(credentials.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > credentials.length) {
            endIndex = credentials.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(credentials.slice(startIndex, endIndex));
    }, [credentials, currPage, itemsPerPage]);

    const togglePasswordVisibility = (index) => {
        setShowPassword(prevState => {
            const updatedShowPassword = [...prevState];
            updatedShowPassword[index] = !updatedShowPassword[index];
            return updatedShowPassword;
        });
    };

    const sendMail = (email, userId, password) => {
        setLoading(true);
        const data = {
            email: email,
            userId: userId,
            password: password
        };

        const apiUrl = `/send-credential-to-mail`;
        authorizedAxiosCommon && authorizedAxiosCommon.post(apiUrl, data)
            .then(response => {
                setTimeout(() => {
                    Swal.fire({ text: response.data.message });
                }, 100);
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            })
            .finally(() => {
                setLoading(false); // End loading
            });
    }

    const handleInputReset = () => {
        setTeacherData(initialForm);
        setCredentials([]);
        setSectionList([]);
    }

    useEffect(() => {
        if (teacherNameSort == true || teacherNameSort == false) {
            setUserIdSort(null);
            setEmailSort(null);
            setGenderSort(null);
        }
    }, [teacherNameSort])

    useEffect(() => {
        if (userIdSort == true || userIdSort == false) {
            setTeacherNameSort(null);
            setEmailSort(null);
            setGenderSort(null);
        }
    }, [userIdSort])

    useEffect(() => {
        if (emailSort == true || emailSort == false) {
            setTeacherNameSort(null);
            setUserIdSort(null);
            setGenderSort(null);
        }
    }, [emailSort])

    useEffect(() => {
        if(genderSort == true || genderSort == false) {
            setEmailSort(null);
            setTeacherNameSort(null);
            setUserIdSort(null);
        }
    })

    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <Header />
                <div className="container-fluid">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb my-0 ms-2">
                            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <span>Dashboard</span>
                            </li>
                            <li className="breadcrumb-item active"><span>Teacher Credentials</span></li>
                        </ol>
                    </nav>
                </div>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fas fa-key"></i>Teacher Credentials</b></h4>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="instituteMasterId" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Institute</label>
                                                    <select class="form-select" aria-label="Default select example" name='instituteMasterId' onChange={handleInsituteChange} value={teacherData.instituteMasterId} >
                                                        <option selected value={''} disabled>Select Institute</option>
                                                        {
                                                            instituteList?.map((institute) => {
                                                                return <option value={institute.id}>{institute.instituteName}</option>
                                                            })
                                                        }
                                                    </select>
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="sectionMasterId" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Section</label>
                                                    <select class="form-select" aria-label="Default select example" name='sectionMasterId' onChange={handleInputChange} value={teacherData.sectionMasterId} >
                                                        <option selected value={''} disabled>Select Section</option>
                                                        {
                                                            sectionList?.map((section) => {
                                                                return <option value={section.id}>{section.sectionName}</option>
                                                            })
                                                        }
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>
                                                <div className="col-auto">
                                                    <button className="btn btn-outline-danger delete_icon" type="button" style={{ marginRight: '20px' }} onClick={handleInputReset}>Reset</button>
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        {/* <div className='d-flex align-items-center justify-content-between'> */}
                                                            {displayedData.length > 0 &&<GeneratePdf id="teacherCredentitals" title={displayedData} headerTitle={"Teacher Credentials"} />}
                                                            <div className='search_btn_row'/* style={{ flexWrap: 'nowrap' }}*/>
                                                                <input
                                                                    className="form-control mr-sm-2"
                                                                    type="Search"
                                                                    placeholder="Search"
                                                                    value={searchValue}
                                                                    onChange={handleEmptySearch}
                                                                    onKeyDown={(event) => {
                                                                        if (event.key === 'Enter') {
                                                                            handleSearch();
                                                                        }
                                                                    }}
                                                                />
                                                                <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                            </div>
                                                        {/* </div> */}
                                                        <div id="teacherCredentitals">
                                                            <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                                <table class="table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th scope="col" className='sl_no'>Sr. No.</th>
                                                                            <th scope="col">
                                                                                Teacher Name
                                                                                <SortIcon sortState={teacherNameSort} setSortState={setTeacherNameSort} setData={setCredentials} setCurrPage={setCurrPage} sortingPath={"teacher.teacherName"} />
                                                                            </th>
                                                                            <th scope="col">
                                                                                Email
                                                                                <SortIcon sortState={emailSort} setSortState={setEmailSort} setData={setCredentials} setCurrPage={setCurrPage} sortingPath={"teacher.email"} />
                                                                            </th>
                                                                            <th scope="col">
                                                                                Contact Number
                                                                            </th>
                                                                            <th scope="col">
                                                                                Gender
                                                                                <SortIcon sortState={genderSort} setSortState={setGenderSort} setData={setCredentials} setCurrPage={setCurrPage} sortingPath={"teacher.gender"} />
                                                                            </th>
                                                                            <th scope="col">
                                                                                User Id
                                                                                <SortIcon sortState={userIdSort} setSortState={setUserIdSort} setData={setCredentials} setCurrPage={setCurrPage} sortingPath={"userId"} />
                                                                            </th>
                                                                            <th scope="col">Password</th>
                                                                            <th scope='col'>Send Mail</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {
                                                                            displayedData?.length > 0 ?
                                                                                displayedData?.map((cred, index) => {
                                                                                    return (
                                                                                        <tr>
                                                                                            <th scope="row" className='sl_no'>{startIndex + index + 1}</th>
                                                                                            <td>{cred.teacher.teacherName}</td>
                                                                                            <td>{cred.teacher.email}</td>
                                                                                            <td>{cred.teacher.contactNumber}</td>
                                                                                            <td>{cred.teacher.gender}</td>
                                                                                            <td>{cred.userId}</td>
                                                                                            <td>
                                                                                                {showPassword[index] ? (cred.password) : ('••••••••')}
                                                                                                <button
                                                                                                    className="btn btn-link"
                                                                                                    type="button"
                                                                                                    onClick={() => togglePasswordVisibility(index)}
                                                                                                >
                                                                                                    <i className={`fa ${showPassword[index] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                                                                </button>
                                                                                            </td>
                                                                                            {/* <td>
                                                                                                <div className={`toggle-container ${cred.adminDeActivate ? '' : 'active'}`}  onClick={()=>changeActive(cred.id , cred.adminDeActivate)}>
                                                                                                    <div className="toggle-background"></div>
                                                                                                    <div className="toggle-switch"></div>
                                                                                                </div>
                                                                                            </td> */}
                                                                                            <td>
                                                                                                <button className="btn btn-outline-info" type="button" onClick={() => sendMail(cred.teacher.email, cred.userId, cred.password)}>
                                                                                                    <i className="fa fa-paper-plane"></i>
                                                                                                </button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    )
                                                                                })
                                                                                :
                                                                                (<tr>
                                                                                    <th colspan={"100%"} style={{ textAlign: 'center' }}>No Credentials Available</th>
                                                                                </tr>)
                                                                        }
                                                                    </tbody>
                                                                </table>
                                                                <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={credentials.length} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default TeacherCredentials;
